import React, { type FC, forwardRef, type ReactNode } from 'react'
import { type ButtonProps, CircularProgress, type SxProps, type Theme, Tooltip } from '@mui/material'

import type { ReadonlyNonEmptyArray } from '../../../utils'

import { type Capability, isTranslation, type Translation } from '../../../domain'

import { Button as BaseButton, IconButton as BaseIconButton } from '../common'
import { FormattedTranslation } from '../Internationalization'
import { useEnabled } from './hooks'

type Props =
    & Readonly<{
        text?: string | Translation
        target?: string
        loading?: boolean
        capabilities?: ReadonlyNonEmptyArray<Capability>
        startIcon: NonNullable<ButtonProps['startIcon']>
        /**
         * ActionButton is supposed to be an IconButton by default.
         * In which case the 'text' is only used in tooltip.
         * Setting 'showText' would display normal Button with startIcon and text.
         */
        showText?: boolean
    }>
    & Pick<ButtonProps, 'id' | 'onClick' | 'variant' | 'disabled' | 'href'>

const suffixStyle: SxProps<Theme> = { ml: 1, my: 'auto' }
const buttonStyle: SxProps<Theme> = { mr: 2 }

export const ActionButton: FC<Props> = ({ text, loading = false, disabled, capabilities, variant = 'secondary', startIcon, showText, ...props }) => {
    const enabled = useEnabled(disabled, capabilities)

    const Caption: ReactNode = isTranslation(text) ? <FormattedTranslation id={text} /> : text
    const Icon: ReactNode = loading ? <CircularProgress sx={suffixStyle} size="1rem" color="inherit" /> : startIcon

    if (showText) {
        return (
            <BaseButton {...props} variant={variant} size="large" sx={buttonStyle} disabled={!enabled} startIcon={Icon}>
                {Caption}
            </BaseButton>
        )
    }

    const IconButton = forwardRef<HTMLButtonElement, ButtonProps>((buttonProps, ref) => (
        <BaseIconButton {...buttonProps} {...props} ref={ref} variant={variant} size="large" sx={buttonStyle} disabled={!enabled}>
            {Icon}
        </BaseIconButton>
    ))

    return (
        <Tooltip placement="bottom" title={Caption}>
            {enabled ? <IconButton /> : (
                <span>
                    <IconButton />
                </span>
            )}
        </Tooltip>
    )
}
